import { useEffect, useState } from "react";
import { toast } from "sonner";
import { User, Mail, Save, X, FileSignature } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import api from "../lib/api";

export default function AdminProfileModal({ onClose }) {
  const [profile, setProfile] = useState({ display_name: "", reply_to_email: "", signature: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/profile")
      .then((r) => setProfile(r.data))
      .catch(() => toast.error("Couldn't load profile"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!profile.display_name?.trim()) { toast.error("Display name required"); return; }
    if (!profile.reply_to_email?.trim()) { toast.error("Reply-to email required"); return; }
    setSaving(true);
    try {
      const res = await api.put("/admin/profile", {
        display_name: profile.display_name,
        reply_to_email: profile.reply_to_email,
        signature: profile.signature,
      });
      setProfile(res.data);
      toast.success("Profile saved. New emails will use these settings.");
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="profile-modal-backdrop">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="profile-modal">
        <div className="bg-navy text-white px-7 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[#10B981]/20 flex items-center justify-center">
              <User className="h-5 w-5 text-[#10B981]" />
            </div>
            <div>
              <div className="font-serif text-xl">Sender Identity</div>
              <div className="text-xs text-white/55">How candidates see emails from {profile.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="profile-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-7 space-y-5 overflow-y-auto">
          {loading ? <div className="text-navy/60">Loading…</div> : (
            <>
              <div>
                <Label htmlFor="dname" className="flex items-center gap-2"><User className="h-3.5 w-3.5 text-[#10B981]" /> Display name <span className="text-navy/40 font-normal">— shown in candidate's "From"</span></Label>
                <Input id="dname" className="mt-2" value={profile.display_name || ""} onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} placeholder="Christopher" data-testid="profile-display-name" />
                <p className="text-[11px] text-navy/45 mt-1.5">They'll see: <span className="font-mono text-navy/70">{(profile.display_name || "Christopher")} · Direct Hire Team &lt;onboarding@send.directhireteam.com&gt;</span></p>
              </div>

              <div>
                <Label htmlFor="rto" className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-[#10B981]" /> Reply-to email <span className="text-navy/40 font-normal">— where their replies land</span></Label>
                <Input id="rto" type="email" className="mt-2" value={profile.reply_to_email || ""} onChange={(e) => setProfile({ ...profile, reply_to_email: e.target.value })} placeholder="christopher@directhireteam.com" data-testid="profile-reply-to" />
                <p className="text-[11px] text-navy/45 mt-1.5">⚠️ Make sure this address exists in Hostinger and forwards to your real inbox.</p>
              </div>

              <div>
                <Label htmlFor="sig" className="flex items-center gap-2"><FileSignature className="h-3.5 w-3.5 text-[#10B981]" /> Email signature <span className="text-navy/40 font-normal">— auto-appended to every email</span></Label>
                <Textarea id="sig" rows={6} className="mt-2 font-mono text-sm leading-relaxed" value={profile.signature || ""} onChange={(e) => setProfile({ ...profile, signature: e.target.value })} placeholder={"Christopher\nDirect Hire Team\n(405) 309-7010\ndirecthireteam.com"} data-testid="profile-signature" />
                <p className="text-[11px] text-navy/45 mt-1.5">Multiline. Will appear at the bottom of every outreach email where {"{signature}"} is in the template.</p>
              </div>

              {/* Preview */}
              <div className="rounded-md border border-navy/10 bg-navy/[0.02] p-4">
                <div className="eyebrow text-navy/55 mb-2">Live preview</div>
                <div className="text-sm text-navy/70 italic mb-3">…(your message above)…</div>
                <div className="text-sm text-navy/85 whitespace-pre-wrap leading-relaxed border-t border-navy/10 pt-3">{profile.signature || "Christopher\nDirect Hire Team\n(405) 309-7010\ndirecthireteam.com"}</div>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-navy/10 px-7 py-4 flex items-center justify-end gap-3 bg-ivory/50">
          <button onClick={onClose} className="text-navy/65 text-sm font-medium hover:text-navy" data-testid="profile-cancel">Cancel</button>
          <button onClick={save} disabled={saving || loading} className="btn-emerald !px-5 !py-2 disabled:opacity-50" data-testid="profile-save">
            {saving ? "Saving…" : <>Save <Save className="h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
